<template>
  <BaseTemplate app-name="Lançar Novidades" page-title="">
    <template #content>
      <div class="px-4 md:px-8 space-y-6">
        <div class="bg-white dark:bg-gray-800 rounded-none md:rounded-none shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6">
          <h1 class="text-2xl font-bold text-gray-800 dark:text-white mb-4">
            Lançar novidade
          </h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Preencha a versão e o texto da novidade. Use asteriscos (*) para destacar trechos em negrito.
            Exemplo: O sistema recebeu *melhorias importantes* na tela de eventos.
          </p>

          <form @submit.prevent="salvarNovidade" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Versão
              </label>
              <input
                v-model="versao"
                type="text"
                required
                class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: 1.2.3"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Texto da novidade
              </label>
              <textarea
                v-model="texto"
                rows="6"
                required
                class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva as novidades desta versão. Use *asteriscos* para trechos em negrito."
              ></textarea>
            </div>

            <div class="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Pré-visualização
              </h2>
              <div
                class="prose prose-strong:font-bold prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-100"
                v-html="previewTexto"
              ></div>
            </div>

            <div class="flex justify-end space-x-3 mt-4">
              <button
                type="button"
                @click="limpar"
                class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Limpar
              </button>
              <button
                type="submit"
                :disabled="salvando"
                class="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {{ salvando ? (editingId ? 'Salvando edição...' : 'Salvando...') : (editingId ? 'Atualizar novidade' : 'Lançar novidade') }}
              </button>
            </div>
          </form>
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-none md:rounded-none shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Histórico de novidades
            </h2>
          </div>

          <div v-if="loading" class="text-gray-500 dark:text-gray-400">
            Carregando novidades...
          </div>

          <div v-else-if="novidades.length === 0" class="text-gray-500 dark:text-gray-400">
            Nenhuma novidade registrada até o momento.
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="item in novidades"
              :key="item.id"
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
            >
              <div class="flex-1">
                <div class="flex items-center justify-between mb-1">
                  <div class="flex items-center space-x-2">
                    <span class="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                      Versão {{ item.versao }}
                    </span>
                    <span
                      v-if="editingId === item.id"
                      class="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                    >
                      Editando
                    </span>
                  </div>
                  <div class="text-right text-xs text-gray-500 dark:text-gray-400">
                    <div>{{ formatDate(item.criado_em) }}</div>
                    <div>{{ formatTime(item.criado_em) }}</div>
                  </div>
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Lançado por: <span class="font-semibold">{{ item.criado_por }}</span>
                </div>
                <div
                  class="prose prose-strong:font-bold prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-100"
                  v-html="item.texto_formatado || formatText(item.texto)"
                ></div>
              </div>
              <div class="flex md:flex-col gap-2 md:ml-4">
                <button
                  type="button"
                  class="px-3 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/70"
                  @click="editarNovidade(item)"
                >
                  Editar
                </button>
                <button
                  type="button"
                  class="px-3 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-800/70"
                  @click="excluirNovidade(item)"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';

const versao = ref('');
const texto = ref('');
const salvando = ref(false);
const novidades = ref([]);
const loading = ref(false);
const editingId = ref(null);

function formatText(text) {
  if (!text) return '';
  const partes = text.split('*');
  let negrito = false;
  const resultado = [];
  for (const parte of partes) {
    if (negrito) {
      resultado.push(`<strong>${parte}</strong>`);
    } else {
      resultado.push(parte);
    }
    negrito = !negrito;
  }
  return resultado.join('');
}

const previewTexto = computed(() => formatText(texto.value));

function limpar() {
  versao.value = '';
  texto.value = '';
  editingId.value = null;
}

function formatDate(isoString) {
  const d = new Date(isoString);
  return d.toLocaleDateString('pt-BR');
}

function formatTime(isoString) {
  const d = new Date(isoString);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

async function carregarNovidades() {
  loading.value = true;
  try {
    const response = await apiClient.get('/api/v1/changelog/novidades');
    novidades.value = response.data || [];
  } catch (error) {
    console.error('Erro ao carregar novidades', error);
  } finally {
    loading.value = false;
  }
}

function editarNovidade(item) {
  versao.value = item.versao;
  texto.value = item.texto;
  editingId.value = item.id;
}

async function excluirNovidade(item) {
  const confirmado = window.confirm(`Deseja realmente excluir a novidade da versão ${item.versao}?`);
  if (!confirmado) return;
  try {
    await apiClient.delete(`/api/v1/changelog/novidades/${item.id}`);
    if (editingId.value === item.id) {
      limpar();
    }
    await carregarNovidades();
  } catch (error) {
    console.error('Erro ao excluir novidade', error);
  }
}

async function salvarNovidade() {
  if (!versao.value || !texto.value) return;
  salvando.value = true;
  try {
    if (editingId.value) {
      await apiClient.put(`/api/v1/changelog/novidades/${editingId.value}`, null, {
        params: {
          versao: versao.value,
          texto: texto.value,
        },
      });
    } else {
      await apiClient.post('/api/v1/changelog/novidades', null, {
        params: {
          versao: versao.value,
          texto: texto.value,
        },
      });
    }
    await carregarNovidades();
    limpar();
  } catch (error) {
    console.error('Erro ao lançar novidade', error);
  } finally {
    salvando.value = false;
  }
}

onMounted(() => {
  carregarNovidades();
});
</script>
